/**
 * Legal Document PDF Generator
 *
 * Generates professionally formatted legal document PDFs using pdf-lib.
 * US Letter format with standard legal document styling.
 */

import { PDFDocument, rgb } from 'pdf-lib';
import {
  loadStandardFonts,
} from './pdfFonts';
import type { PDFFont } from './pdfFonts';
import {
  sanitizeForPdf,
  wrapText,
  safeDrawText,
} from './pdfTextUtils';

// ============================================================================
// Types
// ============================================================================

export interface BuildLegalDocPdfParams {
  /** The full generated document text (markdown) */
  content: string;
  /** Document title */
  title: string;
  /** Creation date */
  createdAt?: Date;
}

// ============================================================================
// Constants
// ============================================================================

// Page dimensions (US Letter)
const PAGE_WIDTH = 612;   // 8.5 inches in points
const PAGE_HEIGHT = 792;  // 11 inches in points

// Margins (1 inch = 72pt)
const MARGIN_LEFT = 72;
const MARGIN_RIGHT = 72;
const MARGIN_TOP = 72;
const MARGIN_BOTTOM = 72;

// Content area
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT; // 468pt

// Font sizes
const FONT_SIZE_TITLE = 16;
const FONT_SIZE_SECTION = 13;
const FONT_SIZE_SUBSECTION = 11;
const FONT_SIZE_BODY = 11;
const FONT_SIZE_FOOTER = 8;

// Line heights
const LINE_HEIGHT_TITLE = 22;
const LINE_HEIGHT_SECTION = 18;
const LINE_HEIGHT_SUBSECTION = 16;
const LINE_HEIGHT_BODY = 16;
const LINE_HEIGHT_FOOTER = 11;

// Spacing
const PARA_SPACING = 10;
const BEFORE_SECTION = 16;
const AFTER_SECTION = 6;
const BEFORE_SUBSECTION = 10;
const AFTER_SUBSECTION = 4;
const BULLET_INDENT = 20;

// Footer
const FOOTER_Y = 30;
const DISCLAIMER_Y = 44;
const CONTENT_BOTTOM_LIMIT = MARGIN_BOTTOM + 20; // 92pt from bottom

// ============================================================================
// Block Types
// ============================================================================

type BlockKind = 'title' | 'section' | 'subsection' | 'bullet' | 'numbered' | 'paragraph';

interface Block {
  kind: BlockKind;
  text: string;
}

// ============================================================================
// Block Parser
// ============================================================================

function stripInlineMarkdown(input: string): string {
  let out = input || '';
  out = out.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  out = out.replace(/\*\*\*(.+?)\*\*\*/g, '$1');
  out = out.replace(/\*\*(.+?)\*\*/g, '$1');
  out = out.replace(/__(.+?)__/g, '$1');
  out = out.replace(/(?<!\w)\*([^*\n]+)\*(?!\w)/g, '$1');
  out = out.replace(/(?<!\w)_([^_\n]+)_(?!\w)/g, '$1');
  out = out.replace(/`+([^`]+)`+/g, '$1');
  out = out.replace(/~~(.+?)~~/g, '$1');
  return out;
}

function parseBlocks(content: string): Block[] {
  const blocks: Block[] = [];
  const lines = (content || '').split('\n');

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    // Title (single #)
    if (line.startsWith('# ') && !line.startsWith('## ')) {
      blocks.push({ kind: 'title', text: stripInlineMarkdown(line.slice(2).trim()) });
      continue;
    }

    // Section header (##)
    if (line.startsWith('## ')) {
      blocks.push({ kind: 'section', text: stripInlineMarkdown(line.slice(3).trim()) });
      continue;
    }

    // Subsection header (###)
    if (line.startsWith('### ')) {
      blocks.push({ kind: 'subsection', text: stripInlineMarkdown(line.slice(4).trim()) });
      continue;
    }

    // Bullet points
    if (/^[-*]\s+/.test(line)) {
      blocks.push({ kind: 'bullet', text: stripInlineMarkdown(line.replace(/^[-*]\s+/, '')) });
      continue;
    }

    // Numbered list
    const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numMatch) {
      blocks.push({ kind: 'numbered', text: stripInlineMarkdown(`${numMatch[1]}. ${numMatch[2]}`) });
      continue;
    }

    // Default: paragraph
    blocks.push({ kind: 'paragraph', text: stripInlineMarkdown(line) });
  }

  return blocks;
}

// ============================================================================
// PDF Builder
// ============================================================================

export async function buildLegalDocPdf(params: BuildLegalDocPdfParams): Promise<File> {
  const createdAt = params.createdAt || new Date();
  const dateStr = createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const pdfDoc = await PDFDocument.create();
  const fonts = await loadStandardFonts(pdfDoc, 'times');
  const regular: PDFFont = fonts.regular;
  const bold: PDFFont = fonts.bold;
  const italic: PDFFont = fonts.italic;

  pdfDoc.setTitle(sanitizeForPdf(params.title, false));
  pdfDoc.setCreationDate(createdAt);
  pdfDoc.setModificationDate(createdAt);
  pdfDoc.setProducer('Legal Document Creator');
  pdfDoc.setCreator('prest0');

  // Parse content into blocks
  const blocks = parseBlocks(params.content);

  // Track pages
  const allPages: ReturnType<typeof pdfDoc.addPage>[] = [];

  // State
  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  allPages.push(page);
  let cursorY = PAGE_HEIGHT - MARGIN_TOP;

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  const addNewPage = (): void => {
    page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    allPages.push(page);
    cursorY = PAGE_HEIGHT - MARGIN_TOP;
  };

  const ensureSpace = (needed: number): void => {
    if (cursorY - needed < CONTENT_BOTTOM_LIMIT) {
      addNewPage();
    }
  };

  const drawText = (
    text: string,
    x: number,
    y: number,
    font: PDFFont,
    size: number,
    color = rgb(0, 0, 0)
  ): void => {
    safeDrawText(page, sanitizeForPdf(text, false), { x, y, size, font, color });
  };

  const drawCenteredText = (
    text: string,
    y: number,
    font: PDFFont,
    size: number,
    color = rgb(0, 0, 0)
  ): void => {
    const sanitized = sanitizeForPdf(text, false);
    let textWidth = 0;
    try {
      textWidth = font.widthOfTextAtSize(sanitized, size);
    } catch {
      textWidth = 0;
    }
    const x = (PAGE_WIDTH - textWidth) / 2;
    safeDrawText(page, sanitized, { x, y, size, font, color });
  };

  const drawWrappedText = (
    text: string,
    font: PDFFont,
    size: number,
    indent: number,
    lineHeight: number,
    bottomPad: number,
  ): void => {
    const sanitized = sanitizeForPdf(text, false);
    const maxWidth = CONTENT_WIDTH - indent;
    const lines = wrapText(sanitized, font, size, maxWidth);
    for (const line of lines) {
      ensureSpace(lineHeight + bottomPad);
      drawText(line, MARGIN_LEFT + indent, cursorY, font, size);
      cursorY -= lineHeight;
    }
    cursorY -= bottomPad;
  };

  // -------------------------------------------------------------------------
  // Render title block at top (centered)
  // -------------------------------------------------------------------------
  const titleSanitized = sanitizeForPdf(params.title, false);
  const titleLines = wrapText(titleSanitized, bold, FONT_SIZE_TITLE, CONTENT_WIDTH);
  for (const line of titleLines) {
    ensureSpace(LINE_HEIGHT_TITLE);
    drawCenteredText(line, cursorY, bold, FONT_SIZE_TITLE);
    cursorY -= LINE_HEIGHT_TITLE;
  }

  // Date line
  cursorY -= 4;
  ensureSpace(LINE_HEIGHT_BODY);
  drawCenteredText(dateStr, cursorY, italic, FONT_SIZE_BODY - 1, rgb(0.3, 0.3, 0.3));
  cursorY -= LINE_HEIGHT_BODY + 8;

  // -------------------------------------------------------------------------
  // Render blocks
  // -------------------------------------------------------------------------
  for (const block of blocks) {
    switch (block.kind) {
      case 'title':
        // Skip inline # title if it duplicates the params.title; render anyway as section-like
        cursorY -= BEFORE_SECTION;
        ensureSpace(LINE_HEIGHT_SECTION + AFTER_SECTION);
        {
          const tLines = wrapText(sanitizeForPdf(block.text, false), bold, FONT_SIZE_TITLE, CONTENT_WIDTH);
          for (const line of tLines) {
            ensureSpace(LINE_HEIGHT_TITLE);
            drawCenteredText(line, cursorY, bold, FONT_SIZE_TITLE);
            cursorY -= LINE_HEIGHT_TITLE;
          }
          cursorY -= AFTER_SECTION;
        }
        break;

      case 'section':
        cursorY -= BEFORE_SECTION;
        {
          const sLines = wrapText(sanitizeForPdf(block.text, false), bold, FONT_SIZE_SECTION, CONTENT_WIDTH);
          for (const line of sLines) {
            ensureSpace(LINE_HEIGHT_SECTION);
            drawText(line, MARGIN_LEFT, cursorY, bold, FONT_SIZE_SECTION);
            cursorY -= LINE_HEIGHT_SECTION;
          }
          cursorY -= AFTER_SECTION;
        }
        break;

      case 'subsection':
        cursorY -= BEFORE_SUBSECTION;
        {
          const ssLines = wrapText(sanitizeForPdf(block.text, false), bold, FONT_SIZE_SUBSECTION, CONTENT_WIDTH);
          for (const line of ssLines) {
            ensureSpace(LINE_HEIGHT_SUBSECTION);
            drawText(line, MARGIN_LEFT, cursorY, bold, FONT_SIZE_SUBSECTION);
            cursorY -= LINE_HEIGHT_SUBSECTION;
          }
          cursorY -= AFTER_SUBSECTION;
        }
        break;

      case 'bullet':
        drawWrappedText('* ' + block.text, regular, FONT_SIZE_BODY, BULLET_INDENT, LINE_HEIGHT_BODY, 3);
        break;

      case 'numbered':
        drawWrappedText(block.text, regular, FONT_SIZE_BODY, BULLET_INDENT, LINE_HEIGHT_BODY, 3);
        break;

      case 'paragraph':
      default:
        drawWrappedText(block.text, regular, FONT_SIZE_BODY, 0, LINE_HEIGHT_BODY, PARA_SPACING);
        break;
    }
  }

  // -------------------------------------------------------------------------
  // Two-pass: stamp page numbers and footer disclaimer on all pages
  // -------------------------------------------------------------------------
  const totalPages = allPages.length;
  const disclaimer = sanitizeForPdf(
    'This document was generated for informational purposes. Consult a licensed attorney.',
    false
  );

  for (let i = 0; i < totalPages; i++) {
    const pg = allPages[i];
    const pageNumText = `Page ${i + 1} of ${totalPages}`;
    const pageNumSanitized = sanitizeForPdf(pageNumText, false);

    // Page number centered
    let pageNumWidth = 0;
    try {
      pageNumWidth = regular.widthOfTextAtSize(pageNumSanitized, FONT_SIZE_FOOTER);
    } catch {
      pageNumWidth = 0;
    }
    const pageNumX = (PAGE_WIDTH - pageNumWidth) / 2;

    safeDrawText(pg, pageNumSanitized, {
      x: pageNumX,
      y: FOOTER_Y,
      size: FONT_SIZE_FOOTER,
      font: regular,
      color: rgb(0.4, 0.4, 0.4),
    });

    // Disclaimer centered above page number
    let disclaimerWidth = 0;
    try {
      disclaimerWidth = italic.widthOfTextAtSize(disclaimer, FONT_SIZE_FOOTER);
    } catch {
      disclaimerWidth = 0;
    }

    // If disclaimer is too wide, truncate or wrap it
    const maxDisclaimerWidth = CONTENT_WIDTH;
    let disclaimerLines: string[];
    if (disclaimerWidth > maxDisclaimerWidth) {
      disclaimerLines = wrapText(disclaimer, italic, FONT_SIZE_FOOTER, maxDisclaimerWidth);
    } else {
      disclaimerLines = [disclaimer];
    }

    let disclaimerY = DISCLAIMER_Y + (disclaimerLines.length - 1) * LINE_HEIGHT_FOOTER;
    for (const dLine of disclaimerLines) {
      let dWidth = 0;
      try {
        dWidth = italic.widthOfTextAtSize(dLine, FONT_SIZE_FOOTER);
      } catch {
        dWidth = 0;
      }
      const dX = (PAGE_WIDTH - dWidth) / 2;
      safeDrawText(pg, dLine, {
        x: dX,
        y: disclaimerY,
        size: FONT_SIZE_FOOTER,
        font: italic,
        color: rgb(0.5, 0.5, 0.5),
      });
      disclaimerY -= LINE_HEIGHT_FOOTER;
    }
  }

  // -------------------------------------------------------------------------
  // Serialize and return as File
  // -------------------------------------------------------------------------
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  // Generate filename from title
  const datePart = createdAt.toISOString().slice(0, 10); // YYYY-MM-DD
  const filenamePart = params.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  const filename = `${filenamePart}-${datePart}.pdf`;

  return new File([blob], filename, { type: 'application/pdf' });
}
